import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
} from "@material-ui/core";

import { Controller, useForm } from "react-hook-form";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { Error } from "../../../common/Error";
import React from "react";
import { Select } from "@ofqual/portal-components/components/Select";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  actionTypesService,
  informationRequestService,
} from "../../../../services";
import {
  ActionTypeCode,
  InformationRequestAction,
  InformationRequestActionViewModel,
} from "../../../../domain/InformationRequestAction";
import { useDispatch, useSelector } from "react-redux";
import { editAction } from "../../../../redux/actions";
import { isFutureDate } from "@ofqual/portal-core/core/utils/dateUtils";
import { ExistingDocuments } from "./ExistingDocuments";
import { authSelector } from "../../../../redux/selectors";
import { modelToJsonPatchDocument } from "@ofqual/portal-core/core/utils/modelHelpers";

// const UPLOAD_DOCS_ACTION_TYPE = 2;

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

export const EditActionDialog: React.FC<{
  onClose: () => void;
  action: InformationRequestAction;
}> = ({ onClose, action }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { token } = useSelector(authSelector);
  const download = (blobId: string) =>
    informationRequestService.downloadAttachment(
      action.informationRequestId,
      action.id,
      blobId,
      {
        token,
      }
    );

  const {
    handleSubmit,
    control,
    errors,
    formState,
  } = useForm<InformationRequestActionViewModel>({
    defaultValues: action,
    shouldUnregister: false,
  });

  const { dirtyFields } = formState;

  const handleCreateAction = (model: InformationRequestActionViewModel) => {
    const document = modelToJsonPatchDocument(
      model,
      (Object.keys(
        dirtyFields
      ) as (keyof InformationRequestActionViewModel)[]).filter(
        (fieldName) => fieldName !== "files"
      )
    );
    dispatch(
      editAction(
        action.informationRequestId,
        action.id,
        model.files,
        // if we are not editing a document-based action, actionAttachments will be undefined in the model
        //  because the control will not havebeen mounted in to the dom
        (model.actionAttachments || [])
          .filter((attachment) => attachment.isToDelete)
          .map((attachment) => attachment.id),
        { document, meta: [] }
      )
    );
    onClose();
  };

  const [actionTypesLookups] = useLookup(actionTypesService);
  const actionTypeCode = actionTypesLookups.find(
    (x) => x.id === action.actionType
  )?.code;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="updated-dialog"
    >
      <DialogTitle data-test="activity-title">Create Action</DialogTitle>
      <Divider />
      <DialogContent>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="actionType"
                rules={{ required: true }}
                as={
                  <Select
                    label="Action Type"
                    datatest="action-type"
                    required
                    disabled={true}
                  >
                    {actionTypesLookups}
                  </Select>
                }
              />
              {errors.actionType && <Error>This field is required</Error>}
            </Grid>
            {actionTypeCode === ActionTypeCode.ReadDocument ? (
              <Grid item xs={12}>
                <ExistingDocuments control={control} download={download} />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="message"
                    rules={{ required: true }}
                    render={({ onChange, value }) => (
                      <>
                        <label>Message:</label>
                        <MarkdownEditor onChange={onChange} value={value} />
                      </>
                    )}
                  />
                </Grid>
                {errors.message && <Error>This field is required</Error>}
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                control={control}
                name="dueDate"
                rules={{
                  required: true,
                  validate: (value) => (isFutureDate(value) ? true : false),
                }}
                as={<DatePicker label="Due Date" required />}
              />
              {errors.dueDate?.type === "required" && (
                <Error>This field is required</Error>
              )}
              {errors.dueDate?.type === "validate" && (
                <Error>Date must be in the future</Error>
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-action-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleCreateAction)}
          color="primary"
          data-test="confirm-create-action-button"
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
